import React, { useState, useEffect } from 'react';
import { X, MessageSquare, Save } from 'lucide-react';

export default function SpecialInstructionsModal({ item, onSave, onClose }) {
  const [instructions, setInstructions] = useState(
    item?.customization?.customInstructions || ''
  );

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        handleSave();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [instructions]);

  const handleSave = () => {
    onSave(instructions.trim());
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg transform animate-slideUp">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-t-2xl p-6">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X size={20} />
          </button>

          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
              <MessageSquare size={24} />
            </div>
            <div>
              <h3 className="text-xl font-bold">Special Instructions</h3>
              <p className="text-sm text-white/80">{item.name}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Instruction Examples */}
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-teal-900 mb-2">💡 Examples:</p>
            <div className="flex flex-wrap gap-2">
              {[
                'Extra spicy',
                'Less oil',
                'No onions',
                'Well done',
                'Medium rare',
                'Extra cheese',
              ].map((example) => (
                <button
                  key={example}
                  onClick={() => setInstructions(example)}
                  className="text-xs bg-white border border-teal-300 text-teal-800 px-2 py-1 rounded hover:bg-teal-100 transition-colors font-medium"
                >
                  {example}
                </button>
              ))}
            </div>
          </div>

          {/* Text Area */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Instructions for Kitchen
            </label>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="E.g., Extra spicy, No onions, Well done..."
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none text-sm"
              rows={4}
              autoFocus
              maxLength={200}
            />
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-slate-500">
                {instructions.length}/200 characters
              </p>
              <p className="text-xs text-slate-500 italic">
                Ctrl+Enter to save
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-4 bg-slate-50 rounded-b-2xl flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-white border-2 border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-2.5 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white font-bold rounded-lg transition-all flex items-center justify-center gap-2 shadow-md"
          >
            <Save size={16} />
            Save Instructions
          </button>
        </div>

        {/* Keyboard Shortcuts Info */}
        <div className="px-4 pb-3 flex items-center justify-center gap-4 text-xs text-slate-500">
          <div className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-white border border-slate-300 rounded shadow-sm font-mono text-[10px]">ESC</kbd>
            <span>Cancel</span>
          </div>
          <div className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-white border border-slate-300 rounded shadow-sm font-mono text-[10px]">Ctrl+Enter</kbd>
            <span>Save</span>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
